import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import type { CommissionStatus } from '../types/commission';

export interface TimelineCommission {
  propertyId: string;
  propertyAddress: string;
  soldPrice: number;
  commission: number;
  shareAmount: number;
  soldDate: string;
  settlementDate: string;
  status: CommissionStatus;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const statusConfig: Record<
  CommissionStatus,
  { color: string; label: string; icon: React.ReactNode }
> = {
  pending: { color: '#ed6c02', label: 'Pending', icon: <ScheduleIcon sx={{ fontSize: 14 }} /> },
  paid: { color: '#2e7d32', label: 'Paid', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  cancelled: { color: '#9e9e9e', label: 'Cancelled', icon: <CancelIcon sx={{ fontSize: 14 }} /> },
};

export interface CommissionTimelineProps {
  properties: TimelineCommission[];
  selectedYear: number;
  onYearChange: (year: number) => void;
  /** Amount field to show in tooltip (default: shareAmount for "My share") */
  amountKey?: 'shareAmount' | 'commission';
  /** Label for amount in tooltip (default: "My share") */
  amountLabel?: string;
  /** When set, clicking a commission dot navigates to the property (e.g. open detail page) */
  onPropertyClick?: (propertyId: string) => void;
}

export default function CommissionTimeline({
  properties,
  selectedYear,
  onYearChange,
  amountKey = 'shareAmount',
  amountLabel = 'My share',
  onPropertyClick,
}: CommissionTimelineProps) {
  const byMonth = useMemo(() => {
    const map: Record<number, TimelineCommission[]> = {};
    for (let m = 1; m <= 12; m++) map[m] = [];

    for (const p of properties) {
      const dateStr = p.settlementDate || p.soldDate;
      const d = dateStr ? new Date(dateStr) : null;
      if (!d || isNaN(d.getTime())) continue;
      if (d.getFullYear() !== selectedYear) continue;
      const month = d.getMonth() + 1;
      map[month].push(p);
    }

    for (let m = 1; m <= 12; m++) {
      map[m].sort(
        (a, b) =>
          new Date(a.settlementDate || a.soldDate).getTime() -
          new Date(b.settlementDate || b.soldDate).getTime()
      );
    }
    return map;
  }, [properties, selectedYear]);

  const hasAny = useMemo(
    () => Object.values(byMonth).some((arr) => arr.length > 0),
    [byMonth]
  );

  const minYear = 2020;
  const maxYear = new Date().getFullYear() + 2;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
          Timeline
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Commissions by month. Navigate years to see when they occurred. Color = status.
        </Typography>

        <Box display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ mb: 2 }}>
          <IconButton
            size="small"
            onClick={() => onYearChange(selectedYear - 1)}
            disabled={selectedYear <= minYear}
            aria-label="Previous year"
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" component="span" sx={{ minWidth: 64, textAlign: 'center' }}>
            {selectedYear}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onYearChange(selectedYear + 1)}
            disabled={selectedYear >= maxYear}
            aria-label="Next year"
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {hasAny ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(12, minmax(52px, 1fr))',
              gap: 0.5,
              overflowX: 'auto',
              width: '100%',
            }}
          >
            {MONTHS.map((label, i) => {
              const month = i + 1;
              const list = byMonth[month] ?? [];
              return (
                <Box
                  key={month}
                  sx={{
                    p: 0.75,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight="600"
                    color="text.secondary"
                    display="block"
                    align="center"
                    sx={{ mb: 0.5 }}
                  >
                    {label}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.25,
                    }}
                  >
                    {list.map((p) => {
                      const cfg = statusConfig[p.status] ?? statusConfig.pending;
                      const dateLabel = p.settlementDate
                        ? new Date(p.settlementDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })
                        : new Date(p.soldDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
                      const amount = p[amountKey];
                      const isClickable = !!onPropertyClick;
                      return (
                        <Tooltip
                          key={p.propertyId}
                          title={
                            <Box>
                              <Typography variant="caption" display="block" fontWeight="600">
                                {p.propertyAddress}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {dateLabel} · {cfg.label}
                              </Typography>
                              <Typography variant="caption" display="block" color="success.light">
                                {amountLabel}: $
                                {typeof amount === 'number'
                                  ? amount.toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                  : '0.00'}
                              </Typography>
                              {isClickable && (
                                <Typography variant="caption" display="block" color="primary.light">
                                  Click to view property
                                </Typography>
                              )}
                            </Box>
                          }
                          placement="top"
                          arrow
                        >
                          <Box
                            component={isClickable ? 'button' : 'div'}
                            type={isClickable ? 'button' : undefined}
                            onClick={isClickable ? () => onPropertyClick(p.propertyId) : undefined}
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: cfg.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              cursor: isClickable ? 'pointer' : 'default',
                              border: 'none',
                              p: 0,
                              '&:hover': { opacity: 0.9 },
                            }}
                          >
                            {list.length <= 3 ? cfg.icon : null}
                          </Box>
                        </Tooltip>
                      );
                    })}
                    {list.length === 0 && (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No commissions in {selectedYear}. Use arrows to switch year.
          </Typography>
        )}

        <Box display="flex" flexWrap="wrap" gap={2} sx={{ mt: 2 }} justifyContent="center">
          {(Object.keys(statusConfig) as CommissionStatus[]).map((s) => {
            const cfg = statusConfig[s];
            return (
              <Box key={s} display="flex" alignItems="center" gap={0.5}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: cfg.color,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {cfg.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}
